import slugifyLib from "@sindresorhus/slugify";

const RICH_TEXT_MARKUP = /<[^>]*>|&(?:amp|lt|gt|quot|#39|nbsp);/g;
const STRUCTURAL_RICH_TEXT_MARKUP = /^<\/?(?:br|p|ul|ol|li)\b/i;
const STRUCTURAL_MARKER = "\u0000";

const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&nbsp;": " ",
};

/**
 * Renders authored rich text (the allow-listed subset in RICH_TEXT_ALLOWED_TAGS)
 * as plain text, for previews and search keys that cannot render markup.
 *
 * Deliberately regex-based rather than parser-based: this runs in the client
 * bundle, and the input is trusted authored content, not user input. Use
 * getPlainTextFromHtml in src/lib/seo.ts for server-side stripping.
 */
export function stripRichTextMarkup(input: string) {
  return input
    .replace(
      RICH_TEXT_MARKUP,
      (match) =>
        HTML_ENTITIES[match] ??
        (STRUCTURAL_RICH_TEXT_MARKUP.test(match) ? STRUCTURAL_MARKER : ""),
    )
    .replace(/\s*\u0000(?:\s*\u0000)*\s*/g, " ")
    .trim();
}

/**
 * Replaces the non-breaking spaces that layout-sensitive data strings carry
 * (see src/data/event-names.test.ts) with plain spaces for machine surfaces.
 */
export function plainSpaces(value: string) {
  return value.replace(/ /g, " ");
}

export function slugify(s: string) {
  return slugifyLib(s, {
    separator: "-",
    lowercase: true,
    decamelize: false,
  });
}

// Search-key normalizer backed by @sindresorhus/slugify.
// We keep case as-is; uFuzzy is case-insensitive by default.
export function normalizeSearch(input: string) {
  const normalizedInput = input.normalize("NFC");
  return slugifyLib(normalizedInput, {
    separator: " ",
    lowercase: false,
    decamelize: false,
    locale: "de",
    preserveLeadingUnderscore: false,
  });
}

const UMLAUT_REPLACEMENTS: Record<string, string> = {
  Ä: "A",
  ä: "a",
  Ö: "O",
  ö: "o",
  Ü: "U",
  ü: "u",
};

export function buildSearchHaystack(input: string) {
  const nfc = input.normalize("NFC");
  if (!/[ÄÖÜäöü]/.test(nfc)) {
    return normalizeSearch(input);
  }
  const normalized = normalizeSearch(input);
  const umlautStrippedInput = nfc.replace(/[ÄÖÜäöü]/g, (match) => {
    return UMLAUT_REPLACEMENTS[match] ?? match;
  });
  const collapsed = normalizeSearch(umlautStrippedInput);
  return collapsed === normalized ? normalized : `${normalized} ${collapsed}`;
}
